import { useRef, useState, useEffect, useCallback } from 'react';
import { KeyCard } from './KeyCard';
import { Skeleton } from '@/components/ui/skeleton';
import type { AccessKey, Device } from '@/types';

interface KeyListProps {
  keys: AccessKey[];
  isLoading: boolean;
  canShareKeys?: boolean;
  onRevoke?: (keyId: string) => void;
  revokingId?: string | null;
  deviceMap?: Record<string, Device>;
  onActiveIndexChange?: (index: number) => void;
}

export function KeyList({ keys, isLoading, canShareKeys, onRevoke, revokingId, deviceMap, onActiveIndexChange }: KeyListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container || keys.length <= 1) return;

    const scrollLeft = container.scrollLeft;
    const cardWidth = container.firstElementChild
      ? (container.firstElementChild as HTMLElement).offsetWidth
      : 320;
    const gap = 16;

    const index = Math.round(scrollLeft / (cardWidth + gap));
    const newIndex = Math.min(index, keys.length - 1);
    setActiveIndex(newIndex);
    onActiveIndexChange?.(newIndex);
  }, [keys.length, onActiveIndexChange]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  if (isLoading) {
    return (
      <div className="flex justify-center">
        <Skeleton className="h-[460px] w-full max-w-sm rounded-2xl" />
      </div>
    );
  }

  if (keys.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 rounded-full bg-muted p-4">
          <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
          </svg>
        </div>
        <p className="text-lg font-medium">Inga aktiva nycklar</p>
        <p className="text-sm text-muted-foreground mt-1">
          Generera en ny nyckel för att låsa upp din enhet
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Carousel container — allow overflow */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-none -mx-4 px-4"
      >
        {keys.map((key) => (
          <div key={key.id} className="snap-center shrink-0 first:snap-start last:snap-end">
            <KeyCard
              accessKey={key}
              device={deviceMap?.[key.device_id]}
              canShareKeys={canShareKeys}
              onRevoke={onRevoke}
              isRevoking={revokingId === key.id}
            />
          </div>
        ))}
      </div>

      {/* Dot indicators */}
      {keys.length > 1 && (
        <div className="flex justify-center gap-1.5">
          {keys.map((_, i) => (
            <button
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === activeIndex
                  ? 'w-6 bg-primary'
                  : 'w-1.5 bg-muted-foreground/30'
              }`}
              onClick={() => {
                const container = scrollRef.current;
                if (!container) return;
                const card = container.children[i] as HTMLElement;
                card?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
              }}
              aria-label={`Nyckel ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
